import { useState } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  AttendanceSettingsTimeSettingsCustomTimeRangeCategoryFragment,
  AttendanceSettingsTimeSettingsCustomTimeRangeCategoryFragmentDoc,
  CreateTimeSettingsCustomTimeRangeCategoryDocument,
  CreateTimeSettingsCustomTimeRangeCategoryMutationVariables,
  TimeSettingsEnumsCategoryTypeEnum,
} from 'generated/resources/graphql'

import SelectOrCreate from 'modules/core/components/SelectOrCreate'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { useAttendanceSettingWorkCategories } from 'modules/attendance/lib/attendanceSettingWorkCategoriesContext'

interface Props {
  label?: string
  timeSettingsCategories: AttendanceSettingsTimeSettingsCustomTimeRangeCategoryFragment[]
  value?: string
  placeholder: string
  error?: string
  onChange: (selectedId: string | undefined) => void
  canCreateSettingCategory: boolean
  categoryType: TimeSettingsEnumsCategoryTypeEnum
}

const CategorySelector: React.FC<Props> = ({
  label,
  placeholder,
  timeSettingsCategories,
  value,
  onChange,
  error = '',
  canCreateSettingCategory,
  categoryType,
}) => {
  const [selectedId, setSelectedId] = useState<string | undefined>(value)
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [creationError, setCreationError] = useState<string>('')
  const { companyId } = useAttendanceSettingWorkCategories()

  const [create] = useMutation(CreateTimeSettingsCustomTimeRangeCategoryDocument, {
    extractorFn: (data) => data.timeSettingsMutations.createTimesettingsCustomTimeRangeCategory,
    update: (cache, { data }) => {
      const category =
        data?.timeSettingsMutations.createTimesettingsCustomTimeRangeCategory
          .customTimeRangeCategory
      if (!category) return

      cache.modify({
        id: `ApiCoreCompany:${companyId}`,
        fields: {
          timeSettingsCustomTimeRangeCategories(existingConnection) {
            const newCategoryRef = cache.writeFragment({
              data: category,
              fragment: AttendanceSettingsTimeSettingsCustomTimeRangeCategoryFragmentDoc,
              fragmentName: 'AttendanceSettingsTimeSettingsCustomTimeRangeCategory',
            })

            return [...existingConnection, newCategoryRef]
          },
        },
      })
    },
  })

  const onSelectorChange = (valueParam: string[]) => {
    const categoryIndex = valueParam[0]
    setSelectedId(categoryIndex)
  }

  const handleCategoryCreate = async (newSelectedValue: string) => {
    const newCategory: CreateTimeSettingsCustomTimeRangeCategoryMutationVariables = {
      name: newSelectedValue ?? '',
      companyId: companyId,
      timeType: 'any_time',
      categoryType,
    }
    const result = await create({ variables: newCategory })
    const handled = handleResult(result, (errors) => {
      if (errors) {
        setCreationError(
          errors?.name
            ? i18n.t(
                'time_tracking.settings.time_blocks.categories.special_hours.wizard.basic_information.name_error'
              )
            : i18n.t('common.error')
        )
      }
    })

    if (handled.success) {
      const category = handled.data.customTimeRangeCategory
      setSelectedId(category?.id)
      onChange(category?.id)
      setCreationError('')
    }
  }

  const onCreateCategory = (name: string) => {
    track(
      categoryType === 'special_hour'
        ? trackingEvents.timeTracking.newSpecialHourNameCreated
        : trackingEvents.timeTracking.newExtraHourNameCreated,
      {
        name,
      }
    )

    handleCategoryCreate(name)
  }

  const creationComponent = canCreateSettingCategory
    ? (name: string) => (
        <Box
          alignItems="center"
          justifyContent="spaceBetween"
          flexDirection="row"
          paddingX="s16"
          paddingY="s6"
        >
          <Text.Normal size="200" weight="medium">
            {name}
          </Text.Normal>
          <Button type="secondary" onClick={() => onCreateCategory(name)}>
            {i18n.t('finance.vendor_selector.create_new')}
          </Button>
        </Box>
      )
    : () => <></>

  return (
    <SelectOrCreate.Popover
      mandatory
      label={label}
      placeholder={placeholder}
      values={!selectedId ? [] : [selectedId]}
      onValuesChange={(newValues) => onChange(newValues[0])}
      inputStyle="badge"
      onToggle={() => setIsOpen(!isOpen)}
      error={error || creationError}
      creationComponent={creationComponent}
      isOpen={isOpen}
      headerLabel={label ?? i18n.t('finance.vendors.title')}
      isMultiSelect={false}
      onChange={onSelectorChange}
    >
      {timeSettingsCategories.map((category) => {
        const { name = '', id } = category ?? {}

        return (
          <SelectOrCreate.Option key={id + '_' + name} label={name} searchValue={name} value={id}>
            {name}
          </SelectOrCreate.Option>
        )
      })}
    </SelectOrCreate.Popover>
  )
}

export default CategorySelector
